import { useState, useEffect, useRef } from 'react';
import { Clock, ExternalLink, Loader2 } from 'lucide-react';
import { useAppStore } from '../../store/appStore';
import { AppIcon } from '../icons/AppIcons';

function formatTimeAgo(date: Date): string {
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return date.toLocaleDateString();
}

export default function ActivityFeed() {
  const { activities } = useAppStore();
  const [displayedActivities, setDisplayedActivities] = useState(10);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Handle infinite scroll
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

      if (scrollPercentage > 0.9 && !isLoadingMore && displayedActivities < activities.length) {
        setIsLoadingMore(true);
        // Simulate loading delay
        setTimeout(() => {
          setDisplayedActivities((prev) => Math.min(prev + 10, activities.length));
          setIsLoadingMore(false);
        }, 500);
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [activities.length, displayedActivities, isLoadingMore]);

  const visibleActivities = activities.slice(0, displayedActivities);

  const getActionColor = (action: string) => {
    switch (action.toLowerCase()) {
      case 'launched':
        return 'text-green-500 bg-green-500/10';
      case 'favorited':
        return 'text-yellow-500 bg-yellow-500/10';
      case 'searched':
        return 'text-blue-500 bg-blue-500/10';
      case 'updated':
        return 'text-purple-500 bg-purple-500/10';
      default:
        return 'text-recipe-accent bg-recipe-accent/10';
    }
  };

  return (
    <div className="bg-recipe-card border border-recipe-border rounded-xl overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-recipe-border">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-recipe-text flex items-center gap-2">
            <Clock className="w-5 h-5 text-recipe-accent" />
            What's Happening
          </h2>
          <span className="text-sm text-recipe-text-muted">
            {activities.length} {activities.length === 1 ? 'event' : 'events'}
          </span>
        </div>
      </div>

      {/* Activity List */}
      <div
        ref={scrollContainerRef}
        className="max-h-[500px] overflow-y-auto scrollbar-thin scrollbar-thumb-recipe-border scrollbar-track-transparent"
      >
        {visibleActivities.length === 0 ? (
          <div className="p-8 text-center">
            <Clock className="w-12 h-12 text-recipe-text-muted mx-auto mb-3" />
            <p className="text-recipe-text-muted">Nothing happening yet</p>
          </div>
        ) : (
          <div className="divide-y divide-recipe-border">
            {visibleActivities.map((activity, index) => (
              <div
                key={activity.id}
                className="p-4 hover:bg-recipe-accent/5 transition-colors group"
                style={{
                  animation: 'slide-up 0.3s ease-out',
                  animationDelay: `${index * 50}ms`,
                  animationFillMode: 'both'
                }}
              >
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 flex items-center justify-center bg-recipe-accent/10 rounded-lg text-recipe-accent">
                      <AppIcon name={activity.icon} size={24} />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <p className="text-sm text-recipe-text">
                          <span className={`font-medium px-2 py-0.5 rounded ${getActionColor(activity.action)}`}>
                            {activity.action}
                          </span>
                          {' '}
                          <span className="font-semibold">{activity.appName}</span>
                        </p>
                        <p className="text-xs text-recipe-text-muted mt-1">
                          {formatTimeAgo(activity.timestamp)}
                        </p>
                      </div>

                      {/* Quick Action */}
                      <button className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-recipe-accent/10 rounded">
                        <ExternalLink className="w-4 h-4 text-recipe-text-muted" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Loading More */}
            {isLoadingMore && (
              <div className="p-4 flex items-center justify-center">
                <Loader2 className="w-5 h-5 text-recipe-accent animate-spin" />
                <span className="ml-2 text-sm text-recipe-text-muted">Loading more...</span>
              </div>
            )}

            {/* End of List */}
            {displayedActivities >= activities.length && activities.length > 10 && (
              <div className="p-4 text-center">
                <p className="text-sm text-recipe-text-muted">
                  You've reached the end of the activity feed
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      {activities.length > 0 && (
        <div className="p-4 border-t border-recipe-border bg-recipe-bg/50">
          <button className="w-full text-sm text-recipe-accent hover:underline font-medium">
            See Everything
          </button>
        </div>
      )}
    </div>
  );
}
